import { useMutation } from '@zg-rentals/react-query5';
import { useDispatch } from 'react-redux';

import useRequestConfig from '../../hooks/useRequestConfig';

import makeRequest from '../../utils/makeRequestUtils';

import { RENTAL_MANAGER_API_V1_PREFIX } from '../../constants/RouteConstants';

import ErrorRMW from '../../model/ErrorRMW';
import { logError } from '@zg-rentals/log-error';

type Props = {
  propertyId: string;
  userToken: string;
  expirationDateTimestamp: string;
  signature: string;
  queryFn: () => Promise<Record<string, unknown>>;
  context?: string;
  replacementSearchPath: string;
  isReminder?: boolean;
};

const checkRequiredParamsAndMutate = async ({
  propertyId,
  userToken,
  expirationDateTimestamp,
  signature,
  queryFn,
  context = '',
  replacementSearchPath,
}: Props) => {
  if (!propertyId || !userToken || !expirationDateTimestamp || !signature) {
    const missingParamList: Array<string> = [];

    if (!propertyId) {
      missingParamList.push('propertyId');
    }

    if (!userToken) {
      missingParamList.push('userToken');
    }

    if (!expirationDateTimestamp) {
      missingParamList.push('expirationDateTimestamp');
    }

    if (!signature) {
      missingParamList.push('signature');
    }

    const errorRMWModel = ErrorRMW({
      code: 'error',
      message: `Missing required parameters: ${missingParamList}. Replacement search path: ${replacementSearchPath}`,
      data: {
        heading: `We couldn't fulfill your request.`,
        message: 'If you need to update your listing, access your account below.',
      },
    });

    logError({
      error: errorRMWModel,
      context: {
        propertyId,
        details: context,
      },
    });

    return Promise.reject(errorRMWModel.data);
  }

  return queryFn?.();
};

const useConfirmAvailability = (propertyId: string, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const mutationFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/availability/confirm`,
      requestConfig,
      config: { params: { propertyId }, timeout: 5000 },
    });

  return useMutation({ mutationFn, ...queryOptions });
};

const useUnauthDeactivate = (
  {
    propertyId,
    userToken,
    expirationDateTimestamp,
    signature,
    replacementSearchPath,
    isReminder,
  }: Omit<Props, 'queryFn'>,
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const params: {
    propertyId: string;
    userToken: string;
    expirationDateTimestamp: string;
    originalSignature: string;
    isReminder?: boolean;
  } = { propertyId, userToken, expirationDateTimestamp, originalSignature: signature };

  if (typeof isReminder !== 'undefined') {
    params.isReminder = isReminder;
  }

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/properties/availability/deactivate`,
      requestConfig,
      config: {
        params,
        timeout: 5000,
      },
    });

  return useMutation({
    mutationFn: () => {
      return checkRequiredParamsAndMutate({
        propertyId,
        userToken,
        expirationDateTimestamp,
        signature,
        queryFn,
        context: 'availability.useUnauthDeactivate.checkRequiredParamsAndMutate',
        replacementSearchPath,
      });
    },
    ...queryOptions,
  });
};

const useUnauthConfirm = (
  {
    propertyId,
    userToken,
    expirationDateTimestamp,
    signature,
    replacementSearchPath,
    isReminder,
  }: Omit<Props, 'queryFn'>,
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const params: {
    propertyId: string;
    userToken: string;
    expirationDateTimestamp: string;
    originalSignature: string;
    isReminder?: boolean;
  } = {
    propertyId,
    userToken,
    expirationDateTimestamp,
    originalSignature: signature,
  };

  if (typeof isReminder !== 'undefined') {
    params.isReminder = isReminder;
  }

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/properties/availability/confirm`,
      requestConfig,
      config: {
        params,
        timeout: 5000,
      },
    });

  return useMutation({
    mutationFn: () => {
      return checkRequiredParamsAndMutate({
        propertyId,
        userToken,
        expirationDateTimestamp,
        signature,
        queryFn,
        context: 'availability.useUnauthConfirm.checkRequiredParamsAndMutate',
        replacementSearchPath,
      });
    },
    ...queryOptions,
  });
};
export { useConfirmAvailability, useUnauthDeactivate, useUnauthConfirm };
